/**
 * Project, test-case and settings page components.
 *
 * Markup contract: `data_flag` root carries data-initial-enabled;
 * `notifications_settings` root carries data-initial-email-disabled /
 * data-initial-inbox-disabled; `schedule_slot` root (a fieldset) carries
 * data-slot and data-preview-url with x-ref cronInput / enabledInput;
 * `access_request_form` carries data-max-length; `persona_selector` /
 * `multi_actor_editor` carry data-env-personas (+ data-initial-env,
 * data-initial-persona-id, data-initial-persona-name, data-initial-actors);
 * `personas_bulk_retry` carries data-retry-targets, data-has-failed,
 * data-stale-count, data-all-failed-stale, data-refresh-url; `token_reveal`
 * carries data-token.
 */
import { env_persona_map, read_int_attr, read_json_attr } from "./helpers.js";

document.addEventListener("alpine:init", () => {
  // Sequential client-driven bulk retry for the personas tab. Each retry
  // re-fires the existing per-row activation endpoint so each row swaps in
  // place via HTMX as its response returns; 200s and 409s both update the
  // row (the global htmx:beforeSwap handler in csrf.js opts 409s into
  // the swap pipeline). After the queue drains, the personas tab partial is
  // re-fetched so the banner counts reflect the new state.
  Alpine.data("personas_bulk_retry", () => ({
    running: false,
    done: 0,
    total: 0,
    targets: [],
    hasFailed: false,
    staleCount: 0,
    allFailedStale: false,
    refreshUrl: "",
    init() {
      this.targets = read_json_attr(this.$el, "retryTargets", []);
      this.total = this.targets.length;
      this.hasFailed = this.$el.dataset.hasFailed === "true";
      this.staleCount = read_int_attr(this.$el, "staleCount", 0);
      this.allFailedStale = this.$el.dataset.allFailedStale === "true";
      this.refreshUrl = this.$el.dataset.refreshUrl || "";
    },
    // Live bulk-retry button label. Mirrors the SSR fallback branches in
    // templates/projects/_personas_tab.html (`_bulk_label`) — keep them in
    // sync to avoid a label flicker on hydration.
    get buttonLabel() {
      if (this.running) return `Retrying ${this.done} / ${this.total}…`;
      if (this.allFailedStale) return `Retry stale (${this.staleCount})`;
      if (this.hasFailed && this.staleCount > 0) {
        return `Retry all failed (${this.staleCount} stale)`;
      }
      return this.hasFailed ? "Retry all failed" : "Retry all";
    },
    async start() {
      if (this.running || this.targets.length === 0) return;
      this.running = true;
      this.done = 0;
      for (let i = 0; i < this.targets.length; i++) {
        const t = this.targets[i];
        try {
          await htmx.ajax("POST", `/projects/${t.project_id}/personas/${t.persona_id}/activate`, {
            target: `#persona-row-${t.persona_id}`,
            swap: "outerHTML",
            source: this.$el,
          });
        } catch (err) {
          // Transport / 5xx — log and keep draining the queue. Per-row 409
          // (PROVISIONING_FAILED) is already handled as a successful swap by
          // the global htmx:beforeSwap handler.
          console.error("personas_bulk_retry: row failed", t, err);
        }
        this.done = i + 1;
      }
      if (this.refreshUrl) {
        try {
          await htmx.ajax("GET", this.refreshUrl, {
            target: "#personas-tab-content",
            swap: "outerHTML",
            source: this.$el,
          });
        } catch (err) {
          console.error("personas_bulk_retry: refresh failed", err);
          this.running = false;
        }
        // On success the banner is re-rendered with fresh state; no need to
        // reset `running` here because this component instance is replaced.
      } else {
        this.running = false;
      }
    },
  }));

  // Project access-request form — character counter for the message field.
  // The `<form>` carries `data-max-length` so the count stays in sync if
  // the limit ever changes server-side.
  Alpine.data("access_request_form", () => ({
    message: "",
    maxLength: 500,
    init() {
      const max = read_int_attr(this.$el, "maxLength", this.maxLength);
      if (max > 0) this.maxLength = max;
    },
    get remaining() {
      return this.maxLength - (this.message || "").length;
    },
  }));

  // CSP-safe replacement for `onchange="this.form.submit()"`. Attach to a
  // <form> via x-data, then `@change="submit"` on the input.
  Alpine.data("submit_on_change", () => ({
    submit(event) {
      const form = event?.target ? event.target.form : null;
      if (form) form.submit();
    },
  }));

  // Project notifications settings — two master switches (email + inbox)
  // each greying out their column when checked. Per-event checkbox values
  // round-trip regardless; the backend short-circuits via the two
  // master flags on NotificationConfig.
  Alpine.data("notifications_settings", () => ({
    emailDisabled: false,
    inboxDisabled: false,
    init() {
      this.emailDisabled = this.$el.dataset.initialEmailDisabled === "true";
      this.inboxDisabled = this.$el.dataset.initialInboxDisabled === "true";
    },
  }));

  Alpine.data("discrepancy_resolve", () => ({
    outcome: "",
    init() {
      const checked = this.$el.querySelector('input[name="outcome"]:checked');
      this.outcome = checked ? checked.value : "";
    },
    notesRequired() {
      return this.outcome === "notes_only";
    },
  }));

  // Hover tooltip for the Exploration Trend chart (issue #1696). Reads
  // data-* attributes from the SVG hit-target (rect/circle) and positions
  // the tooltip relative to the chart's containing block. Click on a mark
  // navigates to that run's detail page.
  Alpine.data("chart_tooltip", () => ({
    visible: false,
    left: 0,
    top: 0,
    label: "",
    cumulative: 0,
    newCount: 0,
    coverage: 0,
    // CSP-safe Alpine cannot evaluate template-literal binding expressions, so
    // expose the style as a getter on the component scope. Return an OBJECT (not
    // a string): Alpine applies object :style via the CSSOM (el.style.x = …),
    // which is CSP-safe, whereas string :style uses setAttribute('style', …),
    // which style-src 'self' blocks.
    get tooltipStyle() {
      return { left: `${this.left}px`, top: `${this.top}px` };
    },
    show(target) {
      const ds = target.dataset;
      this.label = ds.label || "";
      this.cumulative = ds.cumulative || "0";
      this.newCount = ds.new || "0";
      this.coverage = ds.coverage || "0";
      // Position above the hit target, in the containing block's coordinate
      // space (the parent .relative wrapper).
      const rect = target.getBoundingClientRect();
      const parent = target.closest(".relative").getBoundingClientRect();
      this.left = rect.left + rect.width / 2 - parent.left;
      this.top = rect.top - parent.top - 6;
      this.visible = true;
    },
    hide() {
      this.visible = false;
    },
    navigate(target) {
      const href = target.dataset.href;
      if (href) window.location.href = href;
    },
  }));

  Alpine.data("describe_flow", () => ({
    open: true,
    steps: [
      { url: "", action: "" },
      { url: "", action: "" },
      { url: "", action: "" },
    ],
    hide() {
      this.open = false;
    },
    addStep() {
      this.steps.push({ url: "", action: "" });
    },
    // The CSP build cannot assign to a member expression, so each step row
    // writes back through these methods instead of x-model="step.url".
    setStepUrl(step, value) {
      step.url = value;
    },
    setStepAction(step, value) {
      step.action = value;
    },
  }));

  // A boolean the template seeds from `data-initial-enabled` on the scope
  // root and then binds with x-model / x-show. Used wherever a checkbox
  // reveals or greys out a block of form fields.
  Alpine.data("data_flag", () => ({
    enabled: false,
    init() {
      this.enabled = this.$el.dataset.initialEnabled === "true";
    },
  }));

  Alpine.data("persona_selector", () => ({
    selectedEnv: "",
    selectedPersonaId: "",
    selectedPersonaName: "Public User",
    personas: [],
    init() {
      this.selectedEnv = this.$el.dataset.initialEnv || "";
      this.selectedPersonaId = this.$el.dataset.initialPersonaId || "";
      this.selectedPersonaName = this.$el.dataset.initialPersonaName || "Public User";
      Object.assign(this, env_persona_map(this.$el));
      this.personas = this.personasFor(this.selectedEnv);
      if (this.personas.length && !this.selectedPersonaId) {
        this._selectFirst();
      }
    },
    onEnvChange() {
      this.personas = this.personasFor(this.selectedEnv);
      this._selectFirst();
    },
    _selectFirst() {
      if (this.personas.length) {
        this.selectedPersonaId = this.personas[0].id;
        this.selectedPersonaName = this.personas[0].name;
      } else {
        this.selectedPersonaId = "";
        this.selectedPersonaName = "Public User";
      }
    },
    onPersonaChange(id) {
      for (let i = 0; i < this.personas.length; i++) {
        if (this.personas[i].id === id) {
          this.selectedPersonaId = this.personas[i].id;
          this.selectedPersonaName = this.personas[i].name;
          return;
        }
      }
      this._selectFirst();
    },
    personaDescription() {
      for (let i = 0; i < this.personas.length; i++) {
        if (this.personas[i].id === this.selectedPersonaId) {
          return this.personas[i].description || "";
        }
      }
      return "";
    },
  }));

  // Multi-actor editor for the test-case edit form. Authors add/remove
  // role-labelled actor rows (first row = primary). It emits the parallel
  // form fields actor_role / actor_persona_id (static names, repeated) ONLY
  // when >= 2 rows exist; with a single row the form submits just the legacy
  // persona_id / persona_name hidden fields (byte-identical single-actor
  // storage). Built on the persona_selector base shape.
  Alpine.data("multi_actor_editor", () => ({
    selectedEnv: "",
    personas: [],
    actors: [], // [{ role, personaId }]
    init() {
      this.selectedEnv = this.$el.dataset.initialEnv || "";
      Object.assign(this, env_persona_map(this.$el));
      this.personas = this.personasFor(this.selectedEnv);
      const seeded = read_json_attr(this.$el, "initialActors", []);
      if (seeded.length) {
        this.actors = seeded.map((a) => ({ role: a.role, personaId: a.persona_id || "" }));
      } else {
        this.actors = [{ role: "user", personaId: this.$el.dataset.initialPersonaId || "" }];
      }
    },
    onEnvChange() {
      this.personas = this.personasFor(this.selectedEnv);
      const valid = {};
      for (let i = 0; i < this.personas.length; i++) valid[this.personas[i].id] = true;
      for (let j = 0; j < this.actors.length; j++) {
        if (this.actors[j].personaId && !valid[this.actors[j].personaId]) {
          this.actors[j].personaId = "";
        }
      }
    },
    addActor() {
      this.actors.push({ role: "", personaId: "" });
    },
    removeActor(idx) {
      if (idx > 0 && idx < this.actors.length) this.actors.splice(idx, 1);
    },
    setActorPersona(actor, value) {
      actor.personaId = value;
    },
    setActorRole(actor, value) {
      actor.role = value;
    },
    isMultiActor() {
      return this.actors.length >= 2;
    },
    primaryPersonaId() {
      return this.actors.length ? this.actors[0].personaId : "";
    },
    primaryPersonaName() {
      const pid = this.primaryPersonaId();
      for (let i = 0; i < this.personas.length; i++) {
        if (this.personas[i].id === pid) return this.personas[i].name;
      }
      return "Public User";
    },
  }));

  Alpine.data("test_case_bulk", () => ({
    selected: {},
    selectedCount: 0,
    hasSelection: false,
    selectedLabel: "0 selected",
    _updateCounts() {
      this.selectedCount = Object.keys(this.selected).length;
      this.hasSelection = this.selectedCount > 0;
      this.selectedLabel = `${this.selectedCount} selected`;
    },
    toggle(id, checked) {
      if (checked) {
        this.selected[id] = true;
      } else {
        delete this.selected[id];
      }
      this._updateCounts();
    },
    toggleAll(checked) {
      const checkboxes = this.$root.querySelectorAll('tbody input[type="checkbox"]');
      if (checked) {
        for (let i = 0; i < checkboxes.length; i++) {
          this.selected[checkboxes[i].value] = true;
          checkboxes[i].checked = true;
        }
      } else {
        this.selected = {};
        for (let i = 0; i < checkboxes.length; i++) {
          checkboxes[i].checked = false;
        }
      }
      this._updateCounts();
    },
    isSelected(id) {
      return !!this.selected[id];
    },
    allSelected(totalCount) {
      return this.selectedCount > 0 && this.selectedCount === totalCount;
    },
    selectedIdsStr() {
      return Object.keys(this.selected).join(",");
    },
  }));

  Alpine.data("token_reveal", () => ({
    copied: false,
    _token: "",
    _timer: null,
    init() {
      // Pull the plaintext off the DOM immediately so session-replay tools
      // (PostHog, etc.) don't capture it as part of the element snapshot.
      this._token = this.$el.dataset.token || "";
      delete this.$el.dataset.token;
    },
    copy() {
      const token = this._token;
      const done = () => {
        this.copied = true;
        if (this._timer) clearTimeout(this._timer);
        this._timer = setTimeout(() => {
          this.copied = false;
        }, 2000);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard
          .writeText(token)
          .then(done)
          .catch(() => {
            // Fall back silently — clipboard blocked in non-HTTPS contexts.
          });
      }
    },
    dismiss() {
      // Banner is a single-use scope. Empty the whole #token-mint-banner
      // container so the leftover <table> wrapper the OOB row was extracted
      // from (HTMX leaves the parent in place) goes with the visible
      // reveal. Falls back to removing just $root when the banner is
      // rendered outside that container (e.g. legacy tests).
      const host = this.$root.closest("#token-mint-banner");
      if (host) {
        while (host.firstChild) host.removeChild(host.firstChild);
      } else if (this.$root?.remove) {
        this.$root.remove();
      }
    },
    destroy() {
      if (this._timer) clearTimeout(this._timer);
    },
  }));

  // Schedule slot — minimal Alpine wrapper for the project-edit "Schedule"
  // section. State is intentionally minimal: HTMX owns the round-trip and the
  // server is the source of truth for cron correctness; the Alpine component
  // exists only to wire preset buttons to the cron input via x-ref so each
  // preset click also triggers the HTMX preview fetch.
  Alpine.data("schedule_slot", () => ({
    _previewTimer: null,
    cronEmpty: true,
    init() {
      this._syncCronEmpty();
    },
    _syncCronEmpty() {
      const input = this.$refs.cronInput;
      const empty = !input?.value.trim();
      this.cronEmpty = empty;
      // Force-uncheck the enable checkbox when cron is empty so a stale
      // checked state cannot survive into a save that the server would
      // reject ("Enable requires a cron expression").
      if (empty && this.$refs.enabledInput) {
        this.$refs.enabledInput.checked = false;
      }
    },
    onCronInput() {
      this._syncCronEmpty();
      this.queuePreview();
    },
    applyPreset(expr) {
      const input = this.$refs.cronInput;
      if (!input) return;
      input.value = expr;
      this._syncCronEmpty();
      this.refreshPreview();
    },
    // Fire the next-run preview via htmx.ajax with explicit values. We avoid
    // hx-* attributes on the inputs because htmx 1.9.10 crashes
    // (`htmx-internal-data` null deref) on both real keyup events and
    // synthetic dispatchEvent calls when traversing `hx-include="closest
    // fieldset"`. Driving the round-trip from Alpine gives us full control
    // over debouncing, value gathering, and target resolution.
    refreshPreview() {
      const fieldset = this.$root;
      if (!fieldset) return;
      const slot = fieldset.dataset.slot || "";
      const url = fieldset.dataset.previewUrl || "";
      if (!url) return;
      const cronInput = fieldset.querySelector('input[name$="_cron"]');
      const tzInput = fieldset.querySelector('input[name$="_timezone"]');
      if (!cronInput || !tzInput) return;
      const values = {
        cron: cronInput.value,
        timezone: tzInput.value || "UTC",
        slot_name: slot,
      };
      htmx.ajax("POST", url, {
        target: `#preview-${slot}`,
        swap: "innerHTML",
        values: values,
        source: this.$el,
      });
    },
    queuePreview() {
      if (this._previewTimer) clearTimeout(this._previewTimer);
      this._previewTimer = setTimeout(() => {
        this.refreshPreview();
      }, 300);
    },
    destroy() {
      clearTimeout(this._previewTimer);
    },
  }));
});
